
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    MapPin,
    CalendarDays,
    Users,
    Search,
    ChevronDown,
    Minus,
    Plus,
    ShieldCheck,
} from "lucide-react";

const localDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const nextDate = (dateString) => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day + 1);

    return localDate(date);
};

export default function SearchBar() {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        destination: "",
        checkIn: "",
        checkOut: "",
        guests: 2,
    });

    const [rooms, setRooms] = useState(1);
    const [destinations, setDestinations] = useState([]);
    const [guestOpen, setGuestOpen] = useState(false);
    const [error, setError] = useState("");

    const guestRef = useRef(null);

    const today = localDate(new Date());
    const minimumCheckout = nextDate(form.checkIn || today);

    // LOAD DESTINATIONS
    useEffect(() => {
        const controller = new AbortController();

        axios
            .get("https://dubaibudgetivehotels.com/api/destinations", {
                signal: controller.signal,
            })
            .then(({ data }) => {
                setDestinations(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                if (err.code !== "ERR_CANCELED") {
                    console.error("Could not load destinations:", err);
                }
            });

        return () => controller.abort();
    }, []);

    // CLOSE GUEST DROPDOWN
    useEffect(() => {
        const close = (event) => {
            if (
                guestRef.current &&
                !guestRef.current.contains(event.target)
            ) {
                setGuestOpen(false);
            }
        };

        const escape = (event) => {
            if (event.key === "Escape") {
                setGuestOpen(false);
            }
        };

        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", escape);

        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", escape);
        };
    }, []);

    const updateField = (name, value) => {
        setError("");

        setForm((previous) => {
            if (name === "checkIn") {
                return {
                    ...previous,
                    checkIn: value,
                    checkOut:
                        previous.checkOut &&
                            previous.checkOut <= value
                            ? ""
                            : previous.checkOut,
                };
            }

            return { ...previous, [name]: value };
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!form.destination.trim()) {
            return setError("Please select a destination.");
        }

        if (!form.checkIn || !form.checkOut) {
            return setError(
                "Please choose check-in and check-out dates."
            );
        }

        if (
            form.checkIn < today ||
            form.checkOut <= form.checkIn
        ) {
            return setError(
                "Please select valid future stay dates."
            );
        }

        setError("");

        navigate("/search-hotels", {
            state: {
                ...form,
                guests: Number(form.guests),
                rooms,
            },
        });
    };

    const fieldClass = `
    group relative flex min-w-0 flex-col justify-center
    rounded-xl border border-[#DDEBE3]
    bg-[#F7FBF8] px-4 py-3.5
    transition-all duration-200
    hover:border-[#9FD8B6] hover:bg-white
    focus-within:border-[#168B43]
    focus-within:bg-white
    focus-within:ring-2 focus-within:ring-[#168B43]/10
  `;

    const labelClass = `
    mb-2 flex items-center gap-2
    text-[11px] font-bold uppercase
    tracking-[0.06em] text-[#557064]
  `;

    const inputClass = `
    min-h-[27px] w-full min-w-0
    rounded-md bg-transparent
    text-[13px] font-semibold text-[#17324D]
    outline-none
    focus-visible:ring-2
    focus-visible:ring-[#168B43]/30
  `;

    return (
        <form
            onSubmit={handleSubmit}
            className="w-full"
            aria-label="Search hotels"
        >
            {/* SEARCH FORM HEADER */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 px-1">
                <div>
                    <h2 className="text-[17px] font-extrabold tracking-tight text-[#064E3B] sm:text-[20px]">
                        Find Your Perfect Hotel
                    </h2>

                    <p className="mt-1 text-xs text-[#64748B]">
                        Search the best places to stay in Dubai
                    </p>
                </div>

                <div className="hidden items-center gap-2 rounded-full bg-[#E8F7EE] px-3 py-2 text-[11px] font-semibold text-[#168B43] sm:flex">
                    <ShieldCheck size={15} />
                    Secure Hotel Booking
                </div>
            </div>

            {/* SEARCH FIELDS */}
            <div
                className="
          grid grid-cols-1 gap-3
          md:grid-cols-2
          xl:grid-cols-[1.4fr_1fr_1fr_1.1fr_auto]
          xl:items-stretch
        "
            >
                {/* DESTINATION */}
                <label className={fieldClass}>
                    <span className={labelClass}>
                        <MapPin size={16} className="text-[#168B43]" />
                        Destination
                    </span>

                    <span className="relative block">
                        <select
                            name="destination"
                            value={form.destination}
                            onChange={(e) =>
                                updateField("destination", e.target.value)
                            }
                            className={`${inputClass} cursor-pointer appearance-none pr-7`}
                            required
                        >
                            <option value="">Where are you going?</option>

                            {destinations.map((destination) => (
                                <option
                                    key={destination.id ?? destination.name}
                                    value={destination.name}
                                >
                                    {destination.name}
                                </option>
                            ))}
                        </select>

                        <ChevronDown
                            size={16}
                            className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-[#168B43]"
                        />
                    </span>
                </label>

                {/* CHECK IN */}
                <label className={fieldClass}>
                    <span className={labelClass}>
                        <CalendarDays
                            size={16}
                            className="text-[#168B43]"
                        />
                        Check-in
                    </span>

                    <input
                        type="date"
                        name="checkIn"
                        min={today}
                        value={form.checkIn}
                        onChange={(e) =>
                            updateField("checkIn", e.target.value)
                        }
                        className={inputClass}
                        required
                    />
                </label>

                {/* CHECK OUT */}
                <label className={fieldClass}>
                    <span className={labelClass}>
                        <CalendarDays
                            size={16}
                            className="text-[#168B43]"
                        />
                        Check-out
                    </span>

                    <input
                        type="date"
                        name="checkOut"
                        min={minimumCheckout}
                        value={form.checkOut}
                        onChange={(e) =>
                            updateField("checkOut", e.target.value)
                        }
                        className={inputClass}
                        required
                    />
                </label>

                {/* GUESTS & ROOMS */}
                <div
                    className={`${fieldClass} relative`}
                    ref={guestRef}
                >
                    <span className={labelClass}>
                        <Users size={16} className="text-[#168B43]" />
                        Guests & Rooms
                    </span>

                    <button
                        type="button"
                        aria-expanded={guestOpen}
                        aria-controls="guest-selector"
                        onClick={() => setGuestOpen((open) => !open)}
                        className="
              flex min-h-[27px] w-full
              items-center justify-between gap-2
              rounded-md text-left text-[13px]
              font-semibold text-[#17324D]
              focus-visible:outline-2
              focus-visible:outline-[#168B43]
            "
                    >
                        <span>
                            {form.guests}{" "}
                            {form.guests === 1 ? "Guest" : "Guests"},{" "}
                            {rooms} {rooms === 1 ? "Room" : "Rooms"}
                        </span>

                        <ChevronDown
                            size={16}
                            className={`shrink-0 text-[#168B43] transition-transform ${guestOpen ? "rotate-180" : ""
                                }`}
                        />
                    </button>

                    {guestOpen && (
                        <div
                            id="guest-selector"
                            className="
                absolute left-0 right-0 top-full
                z-50 mt-3 min-w-[245px]
                rounded-2xl border border-[#DDEBE3]
                bg-white p-5
                shadow-[0_18px_45px_rgba(6,78,59,0.16)]
              "
                        >
                            {[
                                {
                                    label: "Guests",
                                    value: form.guests,
                                    minimum: 1,
                                    maximum: 12,
                                    set: (value) =>
                                        updateField("guests", value),
                                },
                                {
                                    label: "Rooms",
                                    value: rooms,
                                    minimum: 1,
                                    maximum: 6,
                                    set: setRooms,
                                },
                            ].map((item) => (
                                <div
                                    key={item.label}
                                    className="flex items-center justify-between border-b border-[#EEF3EF] py-3 last:border-0"
                                >
                                    <span className="text-sm font-semibold text-[#17324D]">
                                        {item.label}
                                    </span>

                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            aria-label={`Decrease ${item.label.toLowerCase()}`}
                                            disabled={
                                                item.value <= item.minimum
                                            }
                                            onClick={() =>
                                                item.set(
                                                    Math.max(
                                                        item.minimum,
                                                        item.value - 1
                                                    )
                                                )
                                            }
                                            className="
                        flex h-8 w-8 items-center
                        justify-center rounded-full
                        border border-[#CFE5D6]
                        text-[#168B43]
                        transition hover:bg-[#E8F7EE]
                        disabled:opacity-30
                      "
                                        >
                                            <Minus size={14} />
                                        </button>

                                        <span className="w-5 text-center text-sm font-bold text-[#17324D]">
                                            {item.value}
                                        </span>

                                        <button
                                            type="button"
                                            aria-label={`Increase ${item.label.toLowerCase()}`}
                                            disabled={
                                                item.value >= item.maximum
                                            }
                                            onClick={() =>
                                                item.set(
                                                    Math.min(
                                                        item.maximum,
                                                        item.value + 1
                                                    )
                                                )
                                            }
                                            className="
                        flex h-8 w-8 items-center
                        justify-center rounded-full
                        border border-[#CFE5D6]
                        text-[#168B43]
                        transition hover:bg-[#E8F7EE]
                        disabled:opacity-30
                      "
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => setGuestOpen(false)}
                                className="
                  mt-4 w-full rounded-xl
                  bg-[#168B43] py-2.5
                  text-sm font-semibold text-white
                  transition hover:bg-[#087D37]
                "
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>

                {/* SEARCH BUTTON */}
                <div className="flex xl:py-0">
                    <button
                        type="submit"
                        className="
              flex min-h-[65px] w-full
              items-center justify-center gap-2
              whitespace-nowrap rounded-xl
              bg-[#168B43] px-7
              text-sm font-bold text-white
              shadow-[0_8px_20px_rgba(22,139,67,0.20)]
              transition-all duration-200
              hover:-translate-y-0.5
              hover:bg-[#087D37]
              hover:shadow-[0_12px_25px_rgba(22,139,67,0.28)]
              focus-visible:outline-2
              focus-visible:outline-offset-2
              focus-visible:outline-[#064E3B]
              xl:min-w-[175px]
            "
                    >
                        <Search size={19} />
                        Search Hotels
                    </button>
                </div>
            </div>

            {/* VALIDATION MESSAGE */}
            {error && (
                <p
                    role="alert"
                    className="px-2 pb-1 pt-4 text-sm font-medium text-red-600"
                >
                    {error}
                </p>
            )}
        </form>
    );
}
